/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

import { useState } from "react";
import { exportPNG, exportPDF, exportPPTX } from "../utils/exportMap.js";

// Export dialog: lets the user include/exclude the title + legend, then
// download the current map as PNG, PDF, or PowerPoint (PPTX).
export default function ExportModal({ open, onClose, getSvg, map, distributors, companyLogoUrl }) {
  const [includeTitle, setIncludeTitle] = useState(true);
  const [includeLegend, setIncludeLegend] = useState(true);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [slideFormat, setSlideFormat] = useState("4x3"); // '4x3' | '16x9'
  const [busy, setBusy] = useState(null); // 'png' | 'pdf' | 'pptx'
  const [err, setErr] = useState(null);

  if (!open) return null;

  function buildOpts() {
    const legend = includeLegend
      ? (distributors || [])
          .filter((d) => d.show_on_map !== false)
          .map((d) => ({ label: d.public_name || d.name, color: d.color_hex || "#9ca3af" }))
      : [];
    return {
      title: includeTitle ? (map.map_title || null) : null,
      titleColor: map.map_title_color || "#1f2937",
      legendTextColor: map.map_legend_text_color || null, // null -> auto-contrast vs background
      bgColor: map.map_bg_color || "#ffffff",
      transparent: !!map.map_bg_transparent,
      legend,
      legendPosition: map.map_legend_position === "right" ? "right" : "bottom",
      logoUrl: includeLogo ? (map.map_logo_url || null) : null,
      logoPosition: map.map_logo_position || "top-right",
      companyLogoUrl: companyLogoUrl || null, // account/company logo, top-left of PPTX
      slideFormat,
      fileName: map.name || map.map_title || "distribution-map",
    };
  }

  async function run(kind, fn) {
    const svg = getSvg();
    if (!svg) {
      setErr("Map not ready to export.");
      return;
    }
    setBusy(kind);
    setErr(null);
    try {
      await fn(svg, buildOpts());
    } catch (e) {
      console.error("[export] failed:", e);
      setErr("Export failed. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Export map"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: 24,
          width: 360,
          maxWidth: "92vw",
          boxShadow: "var(--shadow-pop)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Export map</h2>
          <button className="link" aria-label="Close" onClick={onClose} style={{ fontSize: 20 }}>×</button>
        </div>

        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={includeTitle}
              onChange={(e) => setIncludeTitle(e.target.checked)}
              disabled={!map.map_title}
            />
            Include title {map.map_title ? "" : "(none set)"}
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={includeLegend}
              onChange={(e) => setIncludeLegend(e.target.checked)}
            />
            Include legend
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={includeLogo}
              onChange={(e) => setIncludeLogo(e.target.checked)}
              disabled={!map.map_logo_url}
            />
            Include logo {map.map_logo_url ? "" : "(none uploaded)"}
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
          <span style={{ fontSize: 13 }}>PowerPoint slide</span>
          <div className="segmented" role="group" aria-label="PowerPoint slide format">
            {[
              { value: "4x3", label: "4:3" },
              { value: "16x9", label: "16:9" },
            ].map((o) => (
              <button
                key={o.value}
                type="button"
                className={slideFormat === o.value ? "seg on" : "seg"}
                aria-pressed={slideFormat === o.value}
                onClick={() => setSlideFormat(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
          Exports exactly what's on the map now.
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button className="secondary" disabled={!!busy} onClick={() => run("png", exportPNG)} style={{ flex: 1 }}>
            {busy === "png" ? "…" : "PNG"}
          </button>
          <button className="secondary" disabled={!!busy} onClick={() => run("pdf", exportPDF)} style={{ flex: 1 }}>
            {busy === "pdf" ? "…" : "PDF"}
          </button>
          <button className="btn" disabled={!!busy} onClick={() => run("pptx", exportPPTX)} style={{ flex: 1 }}>
            {busy === "pptx" ? "…" : "PowerPoint"}
          </button>
        </div>

        {err && <p className="error" style={{ marginTop: 12 }}>{err}</p>}
      </div>
    </div>
  );
}
